import React, { useEffect, useMemo, useState } from 'react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import DevicesIcon from '@mui/icons-material/Devices'
import LightModeIcon from '@mui/icons-material/LightMode'
import LogoutIcon from '@mui/icons-material/Logout'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Link } from 'react-router'
import cnergiLogo from '../resources/CNERGI_PNG@4x.png'
import {
  clearLoginSession,
  getBoardInfo,
  getLoginSession,
  getProfileDeviceIds,
  getUserDisplayInfo,
  getUserProfile,
} from '../services/authApi'
import './MobileMainDashboardPage.css'

const fallbackMetrics = [
  { label: 'Power Import', value: 'N/A', detail: 'Total import' },
  { label: 'Power Export', value: 'N/A', detail: 'Total export' },
  { label: 'GreenScore', value: 'N/A', detail: 'Average index' },
  { label: 'Savings', value: 'N/A', detail: 'Estimated total' },
]

function unwrapBoardInfo(boardInfo) {
  if (!boardInfo || typeof boardInfo !== 'object') {
    return null
  }

  if (boardInfo.success !== undefined && boardInfo.data && typeof boardInfo.data === 'object') {
    return boardInfo.data
  }

  return boardInfo
}

function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = Number(value.trim())

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function sumValues(values) {
  const numbers = values.filter((value) => value !== null)

  return numbers.length ? numbers.reduce((sum, value) => sum + value, 0) : null
}

function averageValues(values) {
  const numbers = values.filter((value) => value !== null)

  return numbers.length ? numbers.reduce((sum, value) => sum + value, 0) / numbers.length : null
}

function formatKw(value) {
  return value === null ? 'N/A' : `${value.toFixed(2)} kW`
}

function formatScore(value) {
  return value === null ? 'N/A' : value.toFixed(1)
}

function formatPhp(value) {
  return value === null ? 'N/A' : `PHP ${value.toFixed(2)}`
}

function getDeviceId(payload, fallback = 'Device') {
  return payload?.config?.Device?.ID || payload?.data?.DeviceID || payload?.data?.deviceId || payload?.id || fallback
}

function getPowerImport(payload) {
  const data = payload?.data || {}

  return toNumber(data.powerImport ?? payload?.powerImport ?? payload?.devicedata?.meter?.import?.W)
}

function getPowerExport(payload) {
  const data = payload?.data || {}

  return toNumber(data.powerExport ?? payload?.powerExport ?? payload?.devicedata?.meter?.export?.W)
}

function getGreenScore(payload) {
  const data = payload?.data || {}

  return toNumber(data.greenScore ?? payload?.greenScore ?? payload?.ml?.greenScore)
}

function getEstimatedSavings(payload) {
  const data = payload?.data || {}

  return toNumber(
    data.estimatedSavings
      ?? data.estimatedSavingsPhp
      ?? data.savings
      ?? data.savingsPhp
      ?? payload?.estimatedSavings
      ?? payload?.estimatedSavingsPhp
      ?? payload?.savings
      ?? payload?.savingsPhp
  )
}

function getStatusLabel(payload) {
  const statusCode = payload?.ml?.status
  const fault = payload?.ml?.fault

  if (fault !== undefined && String(fault) !== '0') {
    return 'Fault'
  }

  if (String(statusCode) === '2') {
    return 'Warning'
  }

  if (String(statusCode) === '3') {
    return 'Grid'
  }

  return 'Online'
}

function createFallbackBoardInfo(deviceId) {
  return {
    config: {
      Device: {
        ID: deviceId,
      },
    },
    data: {
      deviceId,
    },
  }
}

async function loadBoardInfo(deviceId) {
  try {
    return await getBoardInfo(deviceId)
  } catch (error) {
    console.warn(`Unable to fetch board info for ${deviceId}:`, error)

    return createFallbackBoardInfo(deviceId)
  }
}

function getInitialUser() {
  return getUserDisplayInfo(getLoginSession()?.user)
}

function MobileMainDashboardPage({ isDarkMode = false, onToggleTheme }) {
  const [dashboardUser, setDashboardUser] = useState(getInitialUser)
  const [boardInfos, setBoardInfos] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [message, setMessage] = useState('')

  useEffect(() => {
    let isMounted = true
    const loginSession = getLoginSession()

    if (!loginSession?.user?.email) {
      setIsLoading(false)
      setMessage('Sign in to load your mobile dashboard.')
      return () => {
        isMounted = false
      }
    }

    setDashboardUser(getUserDisplayInfo(loginSession.user))

    getUserProfile(loginSession.user.email)
      .then((profileResponse) => {
        const profileUser = profileResponse?.user || {}
        const profileDeviceIds = getProfileDeviceIds(profileResponse)

        if (isMounted) {
          setDashboardUser(getUserDisplayInfo({ ...loginSession.user, ...profileUser }))
        }

        if (!profileDeviceIds.length) {
          if (isMounted) {
            setBoardInfos([])
            setMessage('No devices are assigned to this account.')
          }

          return []
        }

        return Promise.all(profileDeviceIds.map(loadBoardInfo))
      })
      .then((responses) => {
        if (isMounted && responses?.length) {
          setBoardInfos(responses)
          setMessage('')
        }
      })
      .catch((error) => {
        console.error('Mobile dashboard request failed:', error)

        if (isMounted) {
          setMessage('Unable to load dashboard data.')
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  const payloads = useMemo(() => boardInfos.map(unwrapBoardInfo).filter(Boolean), [boardInfos])
  const metrics = useMemo(() => {
    if (!payloads.length) {
      return fallbackMetrics
    }

    const totalImport = sumValues(payloads.map(getPowerImport))
    const totalExport = sumValues(payloads.map(getPowerExport))
    const averageGreenScore = averageValues(payloads.map(getGreenScore))
    const totalSavings = sumValues(payloads.map(getEstimatedSavings))

    return [
      { label: 'Power Import', value: formatKw(totalImport), detail: 'Total import' },
      { label: 'Power Export', value: formatKw(totalExport), detail: 'Total export' },
      { label: 'GreenScore', value: formatScore(averageGreenScore), detail: 'Average index' },
      { label: 'Savings', value: formatPhp(totalSavings), detail: 'Estimated total' },
    ]
  }, [payloads])

  return (
    <main className="mobile-maindash">
      <header className="mobile-maindash__topbar">
        <Link className="mobile-maindash__back" to="/mobile" aria-label="Back to mobile home">
          <ArrowBackIcon aria-hidden="true" />
        </Link>

        <Link className="mobile-maindash__brand" to="/" aria-label="CNERGI home">
          <img src={cnergiLogo} alt="CNERGI" />
        </Link>

        <button
          className="mobile-maindash__icon-button"
          type="button"
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={onToggleTheme}
        >
          {isDarkMode ? <LightModeIcon aria-hidden="true" /> : <DarkModeIcon aria-hidden="true" />}
        </button>
      </header>

      <section className="mobile-maindash__summary" aria-labelledby="mobile-maindash-title">
        <p>CNERGI EMS</p>
        <h1 id="mobile-maindash-title">Welcome, {dashboardUser.firstName}.</h1>
        <span>{dashboardUser.email || 'Mobile dashboard'}</span>
      </section>

      <section className="mobile-maindash__metrics" aria-label="Energy metrics">
        {metrics.map((metric) => (
          <article className="mobile-maindash__metric" key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
            <small>{metric.detail}</small>
          </article>
        ))}
      </section>

      <section className="mobile-maindash__devices" aria-labelledby="mobile-devices-title">
        <div className="mobile-maindash__section-header">
          <h2 id="mobile-devices-title">Devices</h2>
          <Link to="/maindash/devices">
            <DevicesIcon aria-hidden="true" />
            All
          </Link>
        </div>

        {payloads.length > 0 ? (
          <div className="mobile-maindash__device-list">
            {payloads.map((payload, index) => {
              const deviceId = getDeviceId(payload, `Device ${index + 1}`)

              return (
                <Link
                  className="mobile-maindash__device"
                  key={`${deviceId}-${index}`}
                  to={`/mobile/dashboard/${encodeURIComponent(deviceId)}`}
                >
                  <span>
                    <strong>{deviceId}</strong>
                    <small>{formatKw(getPowerImport(payload))} import</small>
                  </span>
                  <mark data-status={getStatusLabel(payload).toLowerCase()}>
                    {getStatusLabel(payload)}
                  </mark>
                </Link>
              )
            })}
          </div>
        ) : (
          <article className="mobile-maindash__empty">
            <WarningAmberIcon aria-hidden="true" />
            <span>{isLoading ? 'Loading dashboard data...' : message}</span>
          </article>
        )}
      </section>

      <footer className="mobile-maindash__footer">
        <div className="mobile-maindash__user">
          <AccountCircleIcon aria-hidden="true" />
          <span>{dashboardUser.displayName}</span>
        </div>
        <Link to="/mobile/login" onClick={clearLoginSession}>
          <LogoutIcon aria-hidden="true" />
          Logout
        </Link>
      </footer>
    </main>
  )
}

export default MobileMainDashboardPage
